import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withState, withProps, withMethods, patchState, withComputed, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { FormSubjectDto } from '../../Models/FormSubjectDto';
import { DictionaryService } from '../../services/dictionary.service';
import { AppParametersDto } from '../../Models/AppParametersDto';
import { FormService } from '../../services/form.service';
import { PostSendContactFormRequest } from '../../Models/Requests/PostSendContactFormRequest';
import { NotificationService } from '../../services/notification.service';
import { ModalService } from '../../services/modal.service';

export interface ContactFormState {
    subjects: FormSubjectDto[],
    params: AppParametersDto[],
    formSendSuccess: boolean,
    formSendMessage: string,
    isError: boolean;
    isLoadingTopics: boolean,
    isLoading: boolean
  }

const initialContactFormState : ContactFormState = {
  subjects: [],
  params: [],
  formSendSuccess: false,
  formSendMessage: '',
  isError: false,
  isLoadingTopics: false,
  isLoading: false,
};

export const ContactFormModalStore = signalStore(
  { providedIn: 'root' },
  withState(initialContactFormState),
  withProps(() => ({
    dictionaryService: inject(DictionaryService),
    formService: inject(FormService),
    notificationService: inject(NotificationService),
    dialog: inject(ModalService),
  })),
  withComputed(({ params: params, isLoading, isError, formSendSuccess }) => ({
    messageMaxLength: computed(() => {
      return params()?.find(x => x.name === 'maxMessageLength')?.value ?? 256;
    }),
    maxFileSize: computed(() => {
      const ratioByteToMb = 1024 * 1024;
      const maxAttachmentSizeInBytes = Number(params()?.find(x => x.name === 'maxAttachmentSize')?.value ?? 0);
      return maxAttachmentSizeInBytes / ratioByteToMb;
    }),
    notAllowedRegEx: computed(() => {
      const notAllowedRegEx = params()?.find(x => x.name === 'notAllowedAttachmentNameCharsRegExp')?.value ?? '';
      return notAllowedRegEx;
    }),
    maxNameLength: computed(() => {
      const maxNameLength = Number(params()?.find(x => x.name === 'maxAttachmentNameLength')?.value ?? 0);
      return maxNameLength;
    }),
    allowedExtensions: computed(() => {
      const ratioByteToMb = 1024 * 1024;
      const maxAttachmentSizeInBytes = Number(params()?.find(x => x.name === 'maxAttachmentSize')?.value ?? 0);
      return maxAttachmentSizeInBytes / ratioByteToMb;
    }),
    state: computed(() => ({
      isLoading: isLoading(),
      isError: isError(),
      formSendSuccess: formSendSuccess(),
    })),
  })),
  withMethods(({ dictionaryService, formService, notificationService, dialog, ...store }) => ({
    getFormSubjects: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { isLoadingTopics: true });
          return dictionaryService.getFormSubjects().pipe(
            tapResponse({
              next: (subjectsResult) => patchState(store, { subjects: subjectsResult.dictionaries, isLoadingTopics: false }),
              error: () => {
                patchState(store, { isError: true, isLoadingTopics: false });
                notificationService.error('notification.error');
                return;
              },
            }),
          );
        }),
      ),
    ),
    getAppParams: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { isLoading: true });
          return dictionaryService.getAppParams().pipe(
            tapResponse({
              next: (appParamsResult) => patchState(store, { params: appParamsResult.params, isLoading: false }),
              error: () => {
                patchState(store, { isError: true, isLoading: false });
                notificationService.error('notification.error');
                return;
              },
            }),
          );
        }),
      ),
    ),
    sendForm: rxMethod<PostSendContactFormRequest>(
      pipe(
        switchMap((contactForm) => {
          patchState(store, { isLoading: true });
          return formService.sendContactForm(contactForm).pipe(
            tapResponse({
              next: (formSendResult) => {
                patchState(store, { formSendSuccess: formSendResult.result, formSendMessage: formSendResult.message, isLoading: false });
                dialog.contactFormDialogRef.close();
                notificationService.success('notification.success-email');
                return;
              },
              error: () => {
                patchState(store, { isError: true, isLoading: false });
                notificationService.error('notification.error');
                return;
              },
            }),
          );
        }),
      ),
    ),
  })),
  withHooks({
    onInit(store) {
      store.getFormSubjects();
      store.getAppParams();
    },
  }),
);
